import { type PropsWithChildren, type ReactNode, useEffect, useMemo, useState } from 'react';
import type { Lens } from '@hookform/lenses';
import { type Control, type FieldValues, useFormState } from 'react-hook-form';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@interxfi/design-system/components/accordion/accordion';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { STABLE_EMPTY_ARRAY_REF } from '@interxfi/utilities/array/stable-empty';
import { get } from '@interxfi/utilities/object/get';
import { CardFormGroup } from './card-form-group';

interface AccordionFormGroupProps {
  icon?: ReactNode;
  pill?: ReactNode;
  title: string;
}

function AccordionFormGroup({ children, icon, pill, title }: PropsWithChildren<AccordionFormGroupProps>) {
  return (
    <AccordionItem value={title}>
      <AccordionTrigger icon={icon} pill={pill}>
        {title}
      </AccordionTrigger>

      <AccordionContent role="group">
        <VerticalStack>{children}</VerticalStack>
      </AccordionContent>
    </AccordionItem>
  );
}

AccordionFormGroup.Section = CardFormGroup.Section;

// Collapsed accordion panels unmount their inputs, so RHF's focus-first-error can't reveal a failed submit; expand erroring sections instead.
function useFormErrorAccordion<TValues extends object>(
  lens: Lens<TValues>,
  sectionTitles: { readonly [TKey in keyof TValues]?: string },
  defaultExpanded: string[] = STABLE_EMPTY_ARRAY_REF,
): { onValueChange: (value: string[]) => void; value: string[] } {
  const [expanded, setExpanded] = useState<string[]>(defaultExpanded);
  const { control, name } = lens.interop();
  const { errors } = useFormState({ control: control as unknown as Control<FieldValues> });

  const errorSections = useMemo(() => {
    const scopedErrors = name ? get(errors, name) : errors;

    return Object.entries(sectionTitles)
      .filter(([key]) => get(scopedErrors, key))
      .map(([, title]) => title as string);
  }, [errors, name, sectionTitles]);

  useEffect(() => {
    if (!errorSections.length) {
      return;
    }

    setExpanded((previous) => {
      const next = [...new Set([...previous, ...errorSections])];
      return next.length === previous.length ? previous : next;
    });
  }, [errorSections]);

  return { onValueChange: setExpanded, value: expanded };
}

export { AccordionFormGroup, useFormErrorAccordion };
